import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';
import { useAuthStore } from '@shared/stores/authStore';
import { fetchProfile } from '@features/profile/api';

/**
 * Events — canonical member-facing routes (`/events*`, `tenantMiddleware`).
 * RSVP is `POST /events/:id/register` (members register; there is no
 * `/rsvp` endpoint). Responses use the `{ success, data }` envelope.
 *
 * The tenant API returns `name`, `start_date`, `location_name`, etc.
 * This module maps those fields to the resident-app `EventSummary` shape.
 */

export interface EventGuestSummary {
  guest_name?: string | null;
  guest_email?: string | null;
  approval_status?: string | null;
}

export interface EventSummary {
  event_id: string;
  title: string;
  starts_at: string;
  ends_at?: string | null;
  all_day?: boolean;
  location?: string;
  cover_image_url?: string;
  image_url?: string;
  cover_image_key?: string;
  image_key?: string;
  category?: string;
  description?: string;
  rsvp_required?: boolean;
  rsvp_cutoff_date?: string | null;
  rsvp_cutoff_time?: string | null;
  max_attendees?: number | null;
  current_attendees?: number;
  allow_guests?: boolean;
  max_guests?: number | null;
  approval_status?: string | null;
  rsvp_guests?: EventGuestSummary[];
  rsvp_status?: 'yes' | 'maybe' | 'no' | null;
}

export interface EventDetail extends EventSummary {
  description?: string;
  organizer_name?: string;
  capacity?: number;
  attending_count?: number;
}

/** Raw event row from GET /events (paginated or list). */
interface RawEventRow {
  event_id: string;
  name?: string;
  title?: string;
  start_date?: string | Date;
  starts_at?: string;
  end_date?: string | Date | null;
  ends_at?: string | null;
  start_time?: string;
  end_time?: string;
  location_name?: string;
  location?: string;
  event_type_name?: string;
  category?: string;
  description?: string;
  cover_image_url?: string;
  image_url?: string;
  allow_guests?: boolean;
  max_guests?: number | null;
  approval_status?: string | null;
  rsvp_guests?: EventGuestSummary[];
  is_rsvp?: boolean;
  registration_status?: string;
  rsvp_status?: 'yes' | 'maybe' | 'no' | null;
  max_attendees?: number;
  current_attendees?: number;
  rsvp_required?: boolean;
  rsvp_cutoff_date?: string | null;
  rsvp_cutoff_time?: string | null;
}

const DECLINED_EVENTS_STORAGE_PREFIX = 'ip4x_resident_declined_events';

function toIsoTimestamp(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

function resolveRsvpCutoffDeadline(
  cutoffDate: string,
  cutoffTime?: string | null,
): Date | null {
  const base = new Date(cutoffDate);
  if (Number.isNaN(base.getTime())) return null;

  if (cutoffTime) {
    const [hours, minutes] = cutoffTime.split(':').map((value) => parseInt(value, 10));
    return new Date(base.getFullYear(), base.getMonth(), base.getDate(), hours, minutes, 0, 0);
  }

  return new Date(base.getFullYear(), base.getMonth(), base.getDate() + 1, 0, 0, 0, 0);
}

export function isRsvpCutoffReached(event: Pick<EventSummary, 'rsvp_required' | 'rsvp_cutoff_date' | 'rsvp_cutoff_time'>): boolean {
  if (!event.rsvp_required || !event.rsvp_cutoff_date) return false;
  const deadline = resolveRsvpCutoffDeadline(event.rsvp_cutoff_date, event.rsvp_cutoff_time);
  return deadline ? Date.now() >= deadline.getTime() : false;
}

export function isRsvpCapacityReached(
  event: Pick<EventSummary, 'max_attendees' | 'current_attendees'>,
): boolean {
  if (event.max_attendees == null || event.max_attendees <= 0) return false;
  return (event.current_attendees ?? 0) >= event.max_attendees;
}

export function isRsvpClosed(event: EventSummary): boolean {
  return isRsvpCutoffReached(event) || isRsvpCapacityReached(event);
}

function mapRsvpStatus(raw: RawEventRow): 'yes' | 'maybe' | 'no' | null {
  if (raw.approval_status === 'declined' || raw.registration_status === 'declined') {
    return 'no';
  }
  if (raw.rsvp_status === 'yes' || raw.rsvp_status === 'maybe' || raw.rsvp_status === 'no') {
    return raw.rsvp_status;
  }
  if (raw.is_rsvp || raw.registration_status === 'registered' || raw.registration_status === 'pending_payment' || raw.registration_status === 'pending_approval') {
    return 'yes';
  }
  return null;
}

function currentMemberId(): string | undefined {
  const user = useAuthStore.getState().user;
  return user?.memberId ?? user?.member_id ?? undefined;
}

function currentTenantId(): string | undefined {
  const auth = useAuthStore.getState();
  return auth.tenantId ?? auth.user?.tenantId ?? auth.user?.tenant_id ?? undefined;
}

function declinedEventsStorageKey(memberId: string, tenantId: string): string {
  return `${DECLINED_EVENTS_STORAGE_PREFIX}:${tenantId}:${memberId}`;
}

function readDeclinedEventIds(memberId: string, tenantId: string): Set<string> {
  if (typeof localStorage === 'undefined') return new Set<string>();

  try {
    const raw = localStorage.getItem(declinedEventsStorageKey(memberId, tenantId));
    if (!raw) return new Set<string>();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set<string>();
    return new Set(parsed.filter((value): value is string => typeof value === 'string' && value.length > 0));
  } catch {
    return new Set<string>();
  }
}

function writeDeclinedEventIds(memberId: string, tenantId: string, eventIds: Set<string>): void {
  if (typeof localStorage === 'undefined') return;

  try {
    if (eventIds.size === 0) {
      localStorage.removeItem(declinedEventsStorageKey(memberId, tenantId));
      return;
    }

    localStorage.setItem(
      declinedEventsStorageKey(memberId, tenantId),
      JSON.stringify([...eventIds]),
    );
  } catch {
    /* noop */
  }
}

function persistDeclinedEvent(eventId: string, memberId: string, tenantId: string): void {
  const eventIds = readDeclinedEventIds(memberId, tenantId);
  eventIds.add(eventId);
  writeDeclinedEventIds(memberId, tenantId, eventIds);
}

function clearDeclinedEvent(eventId: string, memberId: string, tenantId: string): void {
  const eventIds = readDeclinedEventIds(memberId, tenantId);
  if (!eventIds.delete(eventId)) return;
  writeDeclinedEventIds(memberId, tenantId, eventIds);
}

function applyPersistedDeclineStatus(
  summary: EventSummary,
  memberId?: string,
  tenantId?: string,
): EventSummary {
  if (!memberId || !tenantId) return summary;
  if (summary.rsvp_status === 'yes') {
    clearDeclinedEvent(summary.event_id, memberId, tenantId);
    return summary;
  }

  const declinedEvents = readDeclinedEventIds(memberId, tenantId);
  if (declinedEvents.has(summary.event_id)) {
    return { ...summary, rsvp_status: 'no' };
  }

  return summary;
}

async function resolveCurrentMemberId(): Promise<string | undefined> {
  const existingMemberId = currentMemberId();
  if (existingMemberId) return existingMemberId;

  try {
    const profile = await fetchProfile();
    return profile.memberId ?? profile.member_id ?? undefined;
  } catch {
    return undefined;
  }
}

function isAllDayEvent(raw: RawEventRow, startsAt: string): boolean {
  if (raw.start_time === '00:00' && (!raw.end_time || raw.end_time === '00:00')) {
    return true;
  }
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return false;
  return start.getHours() === 0 && start.getMinutes() === 0;
}

export function mapEventRow(raw: RawEventRow): EventSummary | null {
  const starts_at = toIsoTimestamp(raw.starts_at ?? raw.start_date);
  if (!starts_at) return null;

  const ends_at = toIsoTimestamp(raw.ends_at ?? raw.end_date) ?? null;
  const description = typeof raw.description === 'string' ? raw.description.trim() : '';

  return {
    event_id: raw.event_id,
    title: (raw.title ?? raw.name ?? '').trim() || 'Event',
    starts_at,
    ends_at,
    all_day: isAllDayEvent(raw, starts_at),
    location: raw.location ?? raw.location_name ?? undefined,
    cover_image_url: raw.cover_image_url ?? raw.image_url,
    category: raw.category ?? raw.event_type_name ?? undefined,
    description: description || undefined,
    rsvp_required: raw.rsvp_required === true,
    rsvp_cutoff_date: toIsoTimestamp(raw.rsvp_cutoff_date) ?? null,
    rsvp_cutoff_time: raw.rsvp_cutoff_time ?? null,
    max_attendees: raw.max_attendees ?? null,
    current_attendees: raw.current_attendees ?? 0,
    allow_guests: raw.allow_guests === true,
    max_guests: raw.max_guests ?? null,
    approval_status: raw.approval_status ?? null,
    rsvp_guests: raw.rsvp_guests ?? [],
    rsvp_status: mapRsvpStatus(raw),
  };
}

export async function fetchEvents(
  rangeStart?: string,
  rangeEnd?: string,
  memberId?: string,
): Promise<EventSummary[]> {
  const scopedMemberId = memberId ?? await resolveCurrentMemberId();
  const tenantId = currentTenantId();
  const { data } = await tenantApi.get('/events', {
    params: {
      page: 1,
      limit: 100,
      start_date: rangeStart,
      end_date: rangeEnd,
      ...(scopedMemberId ? { member_id: scopedMemberId } : {}),
    },
  });
  const rows = unwrapList<RawEventRow>(data);
  return rows
    .map(mapEventRow)
    .filter((e): e is EventSummary => e !== null)
    .map((event) => applyPersistedDeclineStatus(event, scopedMemberId, tenantId));
}

export async function fetchEvent(eventId: string, memberId?: string): Promise<EventDetail> {
  const scopedMemberId = memberId ?? await resolveCurrentMemberId();
  const tenantId = currentTenantId();
  const { data } = await tenantApi.get(`/events/${eventId}`, {
    params: scopedMemberId ? { member_id: scopedMemberId } : undefined,
  });
  const raw = unwrap<RawEventRow>(data);
  const summary = mapEventRow(raw);
  if (!summary) {
    throw new Error('Event has no valid start date');
  }
  return {
    ...applyPersistedDeclineStatus(summary, scopedMemberId, tenantId),
    description: raw.description,
    capacity: raw.max_attendees,
    attending_count: raw.current_attendees,
  };
}

export interface RsvpRegisterResult {
  approval_status?: string | null;
}

/**
 * RSVP to an event. The canonical route registers the member; the legacy
 * `status` argument is mapped to the canonical member registration flow.
 */
export async function rsvpEvent(
  eventId: string,
  status: 'yes' | 'no',
  guests: Array<{ name: string; email: string }> = [],
): Promise<RsvpRegisterResult | undefined> {
  const memberId = await resolveCurrentMemberId();
  const tenantId = currentTenantId();
  if (!memberId) {
    throw new Error('No member profile found for RSVP.');
  }

  if (status === 'no') {
    await tenantApi.delete(`/events/${eventId}/members/${memberId}`).catch(() => {
      /* not registered — ignore */
    });
    if (tenantId) {
      persistDeclinedEvent(eventId, memberId, tenantId);
    }
    return undefined;
  }
  const { data } = await tenantApi.post(`/events/${eventId}/register`, {
    member_id: memberId,
    ...(guests.length > 0 ? { guests } : {}),
  });
  const registration = unwrap<RsvpRegisterResult>(data);
  if (tenantId) {
    clearDeclinedEvent(eventId, memberId, tenantId);
  }
  return { approval_status: registration.approval_status ?? null };
}
